import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "../core/telemetry";

/**
 * Root render-failure boundary. Deliberately dependency-free and mounted
 * OUTSIDE every application provider (query client, router, intl, app
 * context) so a provider crash is still caught — which is also why the
 * fallback is plain hardcoded markup rather than localized copy.
 */
export class RootErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportClientError({
      kind: "react-boundary",
      message: error instanceof Error ? error.message : String(error ?? "render failure"),
      stack: error instanceof Error ? (error.stack ?? undefined) : undefined,
      componentStack: info.componentStack ?? undefined,
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" style={{ padding: "3rem 1.5rem", textAlign: "center" }}>
        <h1 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>Something went wrong</h1>
        <p style={{ marginBottom: "1.25rem" }}>The page hit an unexpected error.</p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
